import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Tag, Plus, Trash2, ToggleLeft, ToggleRight, Edit, X, Percent, DollarSign, Clock, Users, Send, Mail } from "lucide-react";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  code: "",
  discount_type: "percent",
  discount_value: 10,
  description: "",
  min_kids: 0,
  max_uses: 0,
  expires_at: "",
  is_active: true,
};

const AdminMarketCampCoupons = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [emailModal, setEmailModal] = useState(null); // { coupon, recipient_email, recipient_name, custom_message }

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/market-camp/coupons`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openEmailModal = (coupon) => {
    setEmailModal({
      coupon,
      recipient_email: "",
      recipient_name: "",
      custom_message: `Hi! We'd like to offer you a discount on Mini Makers Market Camp. Hope to see you there!`,
    });
  };

  const sendCouponEmail = async () => {
    if (!emailModal) return;
    const { coupon, recipient_email, recipient_name, custom_message } = emailModal;
    const email = (recipient_email || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    try {
      await axios.post(`${API}/admin/market-camp/coupons/${coupon.id}/send-email`, {
        recipient_email: email,
        recipient_name: recipient_name || "",
        custom_message: custom_message || "",
      });
      toast.success(`Coupon ${coupon.code} sent to ${email}`);
      setEmailModal(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send coupon email");
    }
  };

  const startCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      code: c.code,
      discount_type: c.discount_type || "percent",
      discount_value: c.discount_value || 0,
      description: c.description || "",
      min_kids: c.min_kids || 0,
      max_uses: c.max_uses || 0,
      expires_at: (c.expires_at || "").split("T")[0],
      is_active: c.is_active !== false,
    });
    setShowForm(true);
  };

  const saveCoupon = async () => {
    if (!form.code.trim()) {
      toast.error("Code is required");
      return;
    }
    if (!form.discount_value || form.discount_value <= 0) {
      toast.error("Discount value must be greater than 0");
      return;
    }
    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      discount_value: parseFloat(form.discount_value),
      min_kids: parseInt(form.min_kids, 10) || 0,
      max_uses: parseInt(form.max_uses, 10) || 0,
      expires_at: form.expires_at ? `${form.expires_at}T23:59:59Z` : "",
    };
    try {
      if (editing) {
        const { code: _c, ...rest } = payload; // code is immutable
        await axios.patch(`${API}/admin/market-camp/coupons/${editing}`, rest);
        toast.success("Coupon updated");
      } else {
        await axios.post(`${API}/admin/market-camp/coupons`, payload);
        toast.success("Coupon created");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ ...EMPTY_FORM });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/market-camp/coupons/${c.id}`, { is_active: !c.is_active });
      fetchData();
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon permanently?")) return;
    try {
      await axios.delete(`${API}/admin/market-camp/coupons/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const isExpired = (c) => {
    if (!c.expires_at) return false;
    try { return new Date(c.expires_at) < new Date(); } catch (e) { return false; }
  };
  const isExhausted = (c) => c.max_uses > 0 && c.used_count >= c.max_uses;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 text-[#F5F5F0]" data-testid="admin-market-camp-coupons-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2">
              <Tag className="w-7 h-7 text-[#D4AF37]" />
              Market Camp · Coupon Codes
            </h1>
            <p className="text-[#A1A1AA] text-sm mt-1">
              Create promo codes parents can enter at checkout. Stack with sibling discounts.
            </p>
          </div>
          <button
            onClick={startCreate}
            className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#050505] font-bold inline-flex items-center gap-2 hover:bg-[#B8943B]"
            data-testid="new-coupon-btn"
          >
            <Plus className="w-4 h-4" /> New Coupon
          </button>
        </div>

        <div className="bg-[#0A0A0A] rounded-2xl border border-[rgba(212,175,55,0.15)] overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-[#A1A1AA]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Tag className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
              <p className="text-[#A1A1AA]">No coupon codes yet.</p>
              <button
                onClick={startCreate}
                className="mt-4 px-4 py-2 rounded-lg bg-[#D4AF37] text-[#050505] font-bold inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Create your first coupon
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#A1A1AA] border-b border-[rgba(212,175,55,0.15)]">
                    <th className="text-left px-3 py-3">Code</th>
                    <th className="text-left px-3 py-3">Discount</th>
                    <th className="text-left px-3 py-3">Usage</th>
                    <th className="text-left px-3 py-3">Limits</th>
                    <th className="text-left px-3 py-3">Status</th>
                    <th className="text-left px-3 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-b border-[rgba(212,175,55,0.08)] hover:bg-[rgba(212,175,55,0.04)]" data-testid={`coupon-row-${c.id}`}>
                      <td className="px-3 py-3 align-top">
                        <p className="font-mono font-black text-[#D4AF37] text-base tracking-wider">{c.code}</p>
                        {c.description && <p className="text-[11px] text-[#A1A1AA] mt-1 max-w-[220px]">{c.description}</p>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="font-bold text-[#F5F5F0] flex items-center gap-1">
                          {c.discount_type === "percent" ? <Percent className="w-3.5 h-3.5" /> : <DollarSign className="w-3.5 h-3.5" />}
                          {c.discount_value}{c.discount_type === "percent" ? "%" : ""}
                        </p>
                        <p className="text-[10px] text-[#A1A1AA] uppercase">{c.discount_type === "percent" ? "Percent off" : "Fixed amount"}</p>
                      </td>
                      <td className="px-3 py-3 align-top text-[#F5F5F0]">
                        <p className="font-bold">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</p>
                        <p className="text-[10px] text-[#A1A1AA]">{c.max_uses ? "uses" : "no limit"}</p>
                      </td>
                      <td className="px-3 py-3 align-top text-[11px] text-[#A1A1AA]">
                        {c.min_kids > 0 && (
                          <p className="flex items-center gap-1"><Users className="w-3 h-3" /> min {c.min_kids} kids</p>
                        )}
                        {c.expires_at ? (
                          <p className={`flex items-center gap-1 ${isExpired(c) ? "text-red-400" : ""}`}>
                            <Clock className="w-3 h-3" /> {(c.expires_at || "").split("T")[0]}
                          </p>
                        ) : <p>no expiry</p>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        {!c.is_active ? <Pill bg="rgba(161,161,170,0.15)" color="#A1A1AA" label="Inactive" />
                         : isExpired(c) ? <Pill bg="rgba(239,68,68,0.15)" color="#EF4444" label="Expired" />
                         : isExhausted(c) ? <Pill bg="rgba(239,68,68,0.15)" color="#EF4444" label="Exhausted" />
                         : <Pill bg="rgba(63,164,55,0.15)" color="#3FA437" label="Active" />}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="flex gap-1.5">
                          <button onClick={() => toggleActive(c)} className="p-1.5 rounded bg-[#27272A] hover:bg-[#3F3F46] text-[#A1A1AA]" title={c.is_active ? "Deactivate" : "Activate"}>
                            {c.is_active ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button onClick={() => openEmailModal(c)} className="p-1.5 rounded bg-blue-500/15 hover:bg-blue-500/25 text-blue-400" title="Email coupon to a recipient" data-testid={`email-coupon-${c.id}`}>
                            <Send className="w-4 h-4" />
                          </button>
                          <button onClick={() => startEdit(c)} className="p-1.5 rounded bg-[#27272A] hover:bg-[#3F3F46] text-[#A1A1AA]" title="Edit" data-testid={`edit-${c.id}`}>
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteCoupon(c.id)} className="p-1.5 rounded bg-red-500/15 hover:bg-red-500/25 text-red-400" title="Delete" data-testid={`delete-${c.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-[#F5F5F0]">
                  {editing ? "Edit Coupon" : "New Coupon"}
                </h3>
                <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <FormField label="Code" required>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g. EARLYBIRD20"
                    disabled={!!editing}
                    maxLength={32}
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] font-mono font-bold tracking-wider focus:border-[#D4AF37] outline-none text-sm uppercase disabled:opacity-50"
                    data-testid="coupon-code"
                  />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Type">
                    <select
                      value={form.discount_type}
                      onChange={(e) => setForm(f => ({ ...f, discount_type: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                      data-testid="coupon-type"
                    >
                      <option value="percent">% Percent off</option>
                      <option value="fixed">$ Fixed amount</option>
                    </select>
                  </FormField>
                  <FormField label={form.discount_type === "percent" ? "Percent" : "Amount ($)"} required>
                    <input
                      type="number"
                      min="0" step="0.01"
                      value={form.discount_value}
                      onChange={(e) => setForm(f => ({ ...f, discount_value: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                      data-testid="coupon-value"
                    />
                  </FormField>
                </div>
                <FormField label="Description (shown to customer)">
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="e.g. 20% off · Early bird through July 1"
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                  />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Min Children (0 = any)">
                    <input
                      type="number" min="0" value={form.min_kids}
                      onChange={(e) => setForm(f => ({ ...f, min_kids: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    />
                  </FormField>
                  <FormField label="Max Uses (0 = unlimited)">
                    <input
                      type="number" min="0" value={form.max_uses}
                      onChange={(e) => setForm(f => ({ ...f, max_uses: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    />
                  </FormField>
                </div>
                <FormField label="Expires (optional)">
                  <input
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => setForm(f => ({ ...f, expires_at: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                  />
                </FormField>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox" checked={form.is_active}
                    onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                    className="w-4 h-4 rounded" style={{ accentColor: "#D4AF37" }}
                  />
                  <span className="text-sm text-[#F5F5F0]">Active</span>
                </label>
              </div>
              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#27272A] text-[#F5F5F0] font-bold hover:bg-[#3F3F46]"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCoupon}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#D4AF37] text-[#050505] font-bold"
                  data-testid="save-coupon"
                >
                  {editing ? "Save changes" : "Create Coupon"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Coupon Modal */}
        {emailModal && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEmailModal(null)} data-testid="email-coupon-modal">
            <div className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-[#F5F5F0] flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" />
                  Email Coupon · <span className="font-mono text-[#D4AF37]">{emailModal.coupon.code}</span>
                </h3>
                <button onClick={() => setEmailModal(null)} className="text-[#A1A1AA] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-[#A1A1AA] mb-4">
                Send the discount code <strong className="text-[#D4AF37]">{emailModal.coupon.code}</strong> directly to someone&apos;s inbox.
              </p>
              <div className="space-y-3">
                <FormField label="Recipient Email" required>
                  <input
                    type="email"
                    value={emailModal.recipient_email}
                    onChange={(e) => setEmailModal(m => ({ ...m, recipient_email: e.target.value }))}
                    placeholder="parent@example.com"
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    data-testid="coupon-email-recipient"
                  />
                </FormField>
                <FormField label="Recipient Name (optional)">
                  <input
                    type="text"
                    value={emailModal.recipient_name}
                    onChange={(e) => setEmailModal(m => ({ ...m, recipient_name: e.target.value }))}
                    placeholder="e.g. Sarah Johnson"
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                  />
                </FormField>
                <FormField label="Personal note (optional)">
                  <textarea
                    rows={3}
                    value={emailModal.custom_message}
                    onChange={(e) => setEmailModal(m => ({ ...m, custom_message: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm resize-none"
                  />
                </FormField>
              </div>
              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => setEmailModal(null)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#27272A] text-[#F5F5F0] font-bold hover:bg-[#3F3F46]"
                >
                  Cancel
                </button>
                <button
                  onClick={sendCouponEmail}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-blue-500 text-white font-bold inline-flex items-center justify-center gap-2"
                  data-testid="coupon-email-send"
                >
                  <Send className="w-4 h-4" /> Send Email
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const Pill = ({ bg, color, label }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider" style={{ background: bg, color }}>
    {label}
  </span>
);

const FormField = ({ label, required, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold mb-1.5">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
  </div>
);

export default AdminMarketCampCoupons;
